// Pagos por asesora: ventas de admisiones de un rango de fechas, su comisión y
// el origen de cada venta (cómo nos conoció, UTM, landing, masivo y días para
// cerrar). Los datos los arma GET /payments/advisers-report en el servidor.
// Por defecto solo cuenta ventas (estudiante nuevo, admisión dividida en varios
// pagos o reingreso tras 6 meses): las mensualidades de estudiantes antiguos a
// veces se registran como admisiones. La regla está en klazia-api utils/sales.
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  ButtonGroup,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  Input,
  InputGroup,
  InputLeftElement,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
} from '@chakra-ui/react';
import { DownloadIcon, SearchIcon } from '@chakra-ui/icons';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import xhr from 'xhr';

import AiPagination from 'components/monitoring/ai/AiPagination';
import { deviceLabel, howFoundLabel } from 'pages/asesoras/prospectos/constants';
import {
  AdIdCell,
  AdviserSelect,
  BRAND,
  Dash,
  KpiTile,
  MATCH_LABEL,
  PAGE_SIZE,
  QuickRanges,
  RANGES,
  cop,
  downloadCsv,
  sourceDetail,
  sourceLabel,
  toInput,
  useActiveAdvisers,
  useUsdToCopRate,
} from './asesoras-shared';

const PagosAsesoras = () => {
  const advisers = useActiveAdvisers();
  const usdToCopRate = useUsdToCopRate();

  const [adviser, setAdviser] = useState('');
  const [from, setFrom] = useState(toInput(dayjs().startOf('month')));
  const [to, setTo] = useState(toInput(dayjs()));
  const [activeRange, setActiveRange] = useState('mes');
  const [query, setQuery] = useState('');
  const [scope, setScope] = useState('nuevas');

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);

  const load = useCallback(async (params) => {
    setError('');
    if (!params.from || !params.to) {
      setError('Elige las fechas «Desde» y «Hasta».');
      return;
    }
    if (params.from > params.to) {
      setError('La fecha «Desde» no puede ser posterior a «Hasta».');
      return;
    }
    try {
      setLoading(true);
      const res = await xhr.get('/payments/advisers-report', { params });
      setRows(res.data.rows || []);
      setPage(1);
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.error?.message || 'No se pudieron cargar los pagos.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Al abrir: pagos del mes actual de todas las asesoras.
  useEffect(() => {
    load({ from, to, adviser });
  }, []);

  const applyRange = (range) => {
    const [start, end] = range.range();
    setFrom(toInput(start));
    setTo(toInput(end));
    setActiveRange(range.key);
    load({ from: toInput(start), to: toInput(end), adviser });
  };

  const handleClear = () => {
    const [start, end] = RANGES[0].range();
    setAdviser('');
    setQuery('');
    setScope('nuevas');
    setFrom(toInput(start));
    setTo(toInput(end));
    setActiveRange('mes');
    load({ from: toInput(start), to: toInput(end), adviser: '' });
  };

  // Valor en COP y comisión de cada pago.
  const computed = useMemo(
    () =>
      rows.map((row) => {
        const amountCop = row.currency === 'USD' ? row.amount * usdToCopRate : row.amount;
        const commissionPercent = row.adviser?.commission || 0;
        return { ...row, amountCop, commission: (amountCop * commissionPercent) / 100, commissionPercent };
      }),
    [rows, usdToCopRate]
  );

  const oldStudentPayments = useMemo(() => computed.filter((row) => !row.is_new_sale).length, [computed]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return computed.filter((row) => {
      if (scope === 'nuevas' && !row.is_new_sale) return false;
      if (!q) return true;
      return [row.student?.name, row.student?.id, row.student?.email, row.adviser?.name, row.program]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(q));
    });
  }, [computed, query, scope]);

  const summary = useMemo(() => {
    const total = filtered.reduce((acc, row) => acc + row.amountCop, 0);
    const commission = filtered.reduce((acc, row) => acc + row.commission, 0);
    const withoutAdviser = filtered.filter((row) => !row.adviser).length;
    const withOrigin = filtered.filter((row) => row.origin).length;
    return { total, commission, count: filtered.length, withoutAdviser, withOrigin };
  }, [filtered]);

  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));

  const exportCsv = () => {
    const header = [
      'Fecha', 'Código estudiante', 'Estudiante', 'Correo', 'Celular', 'Programa', 'Asesora', 'Moneda', 'Valor',
      'Valor (COP)', 'Comisión %', 'Comisión (COP)', 'Cómo nos conoció', 'Fuente', 'Medio', 'Campaña', 'Anuncio',
      'Palabra clave', 'Dispositivo', 'Landing', 'Masivo', 'Días para cerrar', 'Origen encontrado',
      'Tipo', 'Estudiante desde', 'Fecha de contacto', 'Observación', 'Campaña registrada por asesora',
    ];
    const lines = filtered.map((row) => [
      dayjs(row.payment_date).format('DD/MM/YYYY'),
      row.student?.id || '',
      row.student?.name || '',
      row.student?.email || '',
      row.student?.cellphone || '',
      row.program,
      row.adviser?.name || 'Sin asesora',
      row.currency,
      row.amount,
      Math.round(row.amountCop),
      row.commissionPercent,
      Math.round(row.commission),
      row.origin ? howFoundLabel(row.origin.how_found, row.origin.how_found_detail) : '',
      row.origin?.utm_source || '',
      row.origin?.utm_medium || '',
      row.origin?.utm_campaign || '',
      row.origin?.utm_content || '',
      row.origin?.utm_term || '',
      deviceLabel(row.origin?.utm_device),
      row.origin?.landing || '',
      row.origin?.masivo || '',
      row.origin?.days_to_close ?? '',
      row.origin ? { vinculo: 'Vinculado', correo: 'Por correo', celular: 'Por celular' }[row.origin.matched_by] : 'Sin prospecto',
      row.is_new_sale ? 'Venta' : 'Estudiante antiguo',
      row.student_since ? dayjs(row.student_since).format('DD/MM/YYYY') : '',
      row.origin?.contact_date ? dayjs(row.origin.contact_date).format('DD/MM/YYYY') : '',
      row.origin?.observation || '',
      row.origin?.utm_manual ? 'Sí' : '',
    ]);
    downloadCsv(header, lines, `pagos-asesoras_${from}_${to}.csv`);
  };

  return (
    <Box p={{ base: 2, md: 4 }}>
      <Box bg="white" borderRadius="lg" border="1px solid" borderColor="gray.200" overflow="hidden" mb={5}>
        <Flex bg={BRAND} color="white" px={5} py={3} align="center" justify="space-between" gap={3} wrap="wrap">
          <Box>
            <Text fontSize="lg" fontWeight="700">
              Pagos por asesora
            </Text>
            <Text fontSize="xs" opacity={0.85}>
              Ventas de admisiones, comisión y origen de cada venta.
            </Text>
          </Box>
          <Button
            size="sm"
            bg="white"
            color={BRAND}
            leftIcon={<DownloadIcon />}
            _hover={{ bg: 'whiteAlpha.900' }}
            onClick={exportCsv}
            isDisabled={loading || filtered.length === 0}
          >
            Exportar a Excel
          </Button>
        </Flex>

        <Box px={5} py={4}>
          <Grid templateColumns={{ base: '1fr', md: '2fr 1fr 1fr auto' }} gap={3} alignItems="end">
            <FormControl>
              <FormLabel fontSize="sm" color="gray.700">
                Asesora
              </FormLabel>
              <AdviserSelect advisers={advisers} value={adviser} onChange={setAdviser} />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm" color="gray.700">
                Desde
              </FormLabel>
              <Input type="date" value={from} max={to} onChange={(e) => { setFrom(e.target.value); setActiveRange(''); }} />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm" color="gray.700">
                Hasta
              </FormLabel>
              <Input type="date" value={to} min={from} onChange={(e) => { setTo(e.target.value); setActiveRange(''); }} />
            </FormControl>
            <Flex gap={2}>
              <Button bg={BRAND} color="white" _hover={{ bg: '#463fd1' }} onClick={() => load({ from, to, adviser })} isLoading={loading}>
                Buscar
              </Button>
              <Button variant="outline" onClick={handleClear}>
                Limpiar
              </Button>
            </Flex>
          </Grid>

          <QuickRanges active={activeRange} onSelect={applyRange} />

          {error && (
            <Text mt={3} fontSize="sm" color="red.600">
              {error}
            </Text>
          )}
        </Box>
      </Box>

      <Grid templateColumns={{ base: '1fr 1fr', lg: 'repeat(4, 1fr)' }} gap={3} mb={5}>
        <KpiTile label="Ventas" value={cop(summary.total)} hint={`${dayjs(from).format('DD/MM/YYYY')} – ${dayjs(to).format('DD/MM/YYYY')}`} />
        <KpiTile label="Comisión" value={cop(summary.commission)} hint="Según el % de cada asesora" />
        <KpiTile
          label="Pagos"
          value={summary.count}
          hint={[
            summary.withoutAdviser ? `${summary.withoutAdviser} sin asesora` : 'Todos con asesora',
            scope === 'nuevas' && oldStudentPayments ? `${oldStudentPayments} de antiguos sin contar` : null,
          ]
            .filter(Boolean)
            .join(' · ')}
        />
        <KpiTile
          label="Con origen identificado"
          value={summary.count ? `${Math.round((summary.withOrigin / summary.count) * 100)} %` : '—'}
          hint={`${summary.withOrigin} de ${summary.count} con prospecto`}
        />
      </Grid>

      <Box bg="white" borderRadius="lg" border="1px solid" borderColor="gray.200" overflow="hidden">
        <Flex px={5} py={3} align="center" justify="space-between" gap={3} wrap="wrap" borderBottom="1px solid" borderColor="gray.100">
          <Flex gap={3} wrap="wrap" align="center" flex="1">
            <InputGroup maxW="360px" size="sm">
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Buscar estudiante, código, asesora o programa"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setPage(1);
                }}
              />
            </InputGroup>
            <Tooltip
              hasArrow
              label="Ventas = estudiantes nuevos (incluye la admisión pagada en varias partes) y reingresos tras 6 meses sin pagar. «Todos» suma también las mensualidades de estudiantes antiguos registradas como admisiones."
            >
              <ButtonGroup size="sm" isAttached>
                {[
                  { value: 'nuevas', label: 'Ventas' },
                  { value: 'todos', label: 'Todos los pagos' },
                ].map((el) => (
                  <Button
                    key={el.value}
                    colorScheme={scope === el.value ? 'purple' : 'gray'}
                    variant={scope === el.value ? 'solid' : 'outline'}
                    onClick={() => {
                      setScope(el.value);
                      setPage(1);
                    }}
                  >
                    {el.label}
                  </Button>
                ))}
              </ButtonGroup>
            </Tooltip>
          </Flex>
          <Text fontSize="xs" color="gray.500">
            Valores en USD convertidos a COP con tasa {Math.round(usdToCopRate).toLocaleString('es-CO')}
          </Text>
        </Flex>

        {loading ? (
          <Flex justify="center" py={16}>
            <Spinner color={BRAND} />
          </Flex>
        ) : filtered.length === 0 ? (
          <Box textAlign="center" py={14} px={4}>
            <Text fontWeight="600" color="gray.700">
              No hay pagos para estos filtros
            </Text>
            <Text fontSize="sm" color="gray.500" mt={1}>
              Prueba con otro rango de fechas o con «Todas las asesoras».
            </Text>
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th colSpan={6} bg="gray.50" color="gray.600" borderColor="gray.200">
                      Pago
                    </Th>
                    <Th colSpan={7} bg="purple.50" color="purple.700" borderColor="purple.100">
                      Origen de la venta
                    </Th>
                  </Tr>
                  <Tr>
                    <Th bg="gray.50">Fecha</Th>
                    <Th bg="gray.50">Estudiante</Th>
                    <Th bg="gray.50">Programa</Th>
                    <Th bg="gray.50">Asesora</Th>
                    <Th bg="gray.50" isNumeric>
                      Valor
                    </Th>
                    <Th bg="gray.50" isNumeric>
                      Comisión
                    </Th>
                    <Th bg="purple.50">Cómo nos conoció</Th>
                    <Th bg="purple.50">Fuente (UTM)</Th>
                    <Th bg="purple.50">ID anuncio</Th>
                    <Th bg="purple.50">Landing</Th>
                    <Th bg="purple.50">Masivo</Th>
                    <Th bg="purple.50" isNumeric>
                      Días para cerrar
                    </Th>
                    <Th bg="purple.50">Observación</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {pageRows.map((row) => (
                    <Tr key={row.id} _hover={{ bg: 'gray.50' }}>
                      <Td whiteSpace="nowrap">{dayjs(row.payment_date).format('DD/MM/YYYY')}</Td>
                      <Td minW="200px" whiteSpace="normal">
                        {row.student ? (
                          <>
                            <Link to={`/plataforma/administrador/ver-estudiante/${row.student.id}`}>
                              <Text fontWeight="600" color="gray.800" _hover={{ color: BRAND, textDecoration: 'underline' }}>
                                {row.student.name || 'Sin nombre'}
                              </Text>
                              <Text fontSize="xs" color="gray.500">
                                Código {row.student.id}
                              </Text>
                            </Link>
                            {!row.is_new_sale && (
                              <Badge mt={1} colorScheme="gray" variant="subtle" textTransform="none" fontWeight="500">
                                Estudiante antiguo
                                {row.student_since ? ` · desde ${dayjs(row.student_since).format('MM/YYYY')}` : ''}
                              </Badge>
                            )}
                          </>
                        ) : (
                          <Dash />
                        )}
                      </Td>
                      <Td minW="160px" whiteSpace="normal">
                        {row.program || <Dash />}
                      </Td>
                      <Td minW="150px" whiteSpace="normal">
                        {row.adviser ? (
                          row.adviser.name
                        ) : (
                          <Badge colorScheme="orange" variant="subtle" textTransform="none">
                            Sin asesora
                          </Badge>
                        )}
                      </Td>
                      <Td isNumeric sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {cop(row.amountCop)}
                        {row.currency === 'USD' && (
                          <Text fontSize="xs" color="gray.500">
                            USD {row.amount.toLocaleString('es-CO')}
                          </Text>
                        )}
                      </Td>
                      <Td isNumeric sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {row.commissionPercent > 0 ? (
                          <>
                            {cop(row.commission)}
                            <Text fontSize="xs" color="gray.500">
                              {row.commissionPercent} %
                            </Text>
                          </>
                        ) : (
                          <Dash />
                        )}
                      </Td>
                      <Td minW="170px" whiteSpace="normal">
                        {row.origin ? (
                          <>
                            <Text>{howFoundLabel(row.origin.how_found, row.origin.how_found_detail)}</Text>
                            {MATCH_LABEL[row.origin.matched_by] && (
                              <Tooltip
                                hasArrow
                                label="El estudiante no está vinculado a un prospecto; se encontró uno con su mismo correo o celular."
                              >
                                <Badge mt={1} colorScheme="gray" variant="subtle" textTransform="none" fontWeight="500">
                                  {MATCH_LABEL[row.origin.matched_by]}
                                </Badge>
                              </Tooltip>
                            )}
                          </>
                        ) : (
                          <Text fontSize="xs" color="gray.400">
                            Sin prospecto
                          </Text>
                        )}
                      </Td>
                      <Td minW="140px" whiteSpace="normal">
                        {sourceLabel(row.origin) ? (
                          <>
                            <Text>{sourceLabel(row.origin)}</Text>
                            {sourceDetail(row.origin) && (
                              <Text fontSize="xs" color="gray.500">
                                {sourceDetail(row.origin)}
                              </Text>
                            )}
                          </>
                        ) : (
                          <Dash />
                        )}
                      </Td>
                      <Td minW="150px" whiteSpace="normal">
                        <AdIdCell origin={row.origin} />
                      </Td>
                      <Td minW="150px" whiteSpace="normal" wordBreak="break-all">
                        {row.origin?.landing ? <Text fontSize="xs">{row.origin.landing}</Text> : <Dash />}
                      </Td>
                      <Td minW="140px" whiteSpace="normal">
                        {row.origin?.masivo || <Dash />}
                      </Td>
                      <Td isNumeric sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {row.origin?.days_to_close != null ? `${row.origin.days_to_close} d` : <Dash />}
                      </Td>
                      <Td minW="260px" maxW="360px" whiteSpace="normal">
                        {row.origin?.observation ? (
                          <Tooltip hasArrow placement="top-start" label={row.origin.observation}>
                            <Text fontSize="sm" noOfLines={3}>
                              {row.origin.observation}
                            </Text>
                          </Tooltip>
                        ) : (
                          <Dash />
                        )}
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
            <AiPagination
              pagination={{ page, pageSize: PAGE_SIZE, total: filtered.length, pageCount }}
              noun="pagos"
              onPageChange={setPage}
            />
          </>
        )}
      </Box>
    </Box>
  );
};

export default PagosAsesoras;
